import React, { FunctionComponent } from 'react';
import {
  Chip,
  Grid,
  Rating,
  RatingProps,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import moment from 'moment';

import {
  CustomCardContent,
  defaultRatingStyles,
  EmptyRateIcon,
  RateIcon,
} from './helper';
import { ILessonTutorFeedback } from '../../../store/V2/LessonTutorFeedbacksSlice';
import { getFormatedDurationByMinutes, getShortName } from '../../../helper';
import { overallOutOfRating } from './AddTutorFeedbackForm';
import { IUser } from '../../../store/UserSlice';

interface ViewTutorFeedbackFormProps {
  feedback: ILessonTutorFeedback;
}

const commonRatingProps: RatingProps = {
  icon: RateIcon,
  emptyIcon: EmptyRateIcon,
};

const ViewTutorFeedbackForm: FunctionComponent<ViewTutorFeedbackFormProps> = ({
  feedback,
}) => {
  const feedbackTutor: IUser | undefined = feedback.tutor as IUser;
  const feedbackRewards = feedback.feedbackRewards || [];
  const createdAt = moment(feedback.createdAt).format(
    'ddd MMM DD YYYY, hh:mm A'
  );

  const lastUpdatedAt = moment(feedback.updatedAt).format(
    'ddd MMM DD YYYY, hh:mm A'
  );

  let lessonExtension = '';
  let lessonExtensionReason = '';

  if (feedback.lessonExtension) {
    lessonExtension = getFormatedDurationByMinutes({
      minutes: feedback.lessonExtension,
    });

    if (feedback.lessonExtensionReason) {
      lessonExtensionReason = feedback.lessonExtensionReason;
    }
  }

  return (
    <div>
      <Grid container rowSpacing="10px">
        {feedback.overallStudentRating ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Typography fontSize="16px" fontWeight={600} textAlign="center">
                Overall rating
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
              >
                <Typography fontWeight={900} fontSize="30px">
                  <span style={{ fontSize: '50px' }}>
                    {feedback.overallStudentRating || 0}
                  </span>
                  /{overallOutOfRating}
                </Typography>
                <Rating
                  readOnly
                  {...commonRatingProps}
                  defaultValue={feedback.overallStudentRating}
                  sx={{ ml: '10px', ...defaultRatingStyles }}
                />
              </Stack>
              {feedbackRewards.length ? (
                <Stack
                  gap="5px"
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="center"
                >
                  {feedbackRewards.map((item) => {
                    const chipStyles: SxProps = {
                      fontWeight: 500,
                      fontSize: '12px',
                      borderRadius: '20px',
                      backgroundColor: '#01A79F',
                      color: '#F8FAFC',
                      '&:hover': {
                        backgroundColor: '#01A79F',
                        color: '#F8FAFC',
                      },
                    };

                    return <Chip key={item} label={item} sx={chipStyles} />;
                  })}
                </Stack>
              ) : null}
            </CustomCardContent>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Student attendance
              </Typography>
              <Typography fontSize="14px" fontWeight={500} color="#647488">
                {feedback.isStudentAttended === 'Yes'
                  ? 'Attended'
                  : 'Not Attended'}
              </Typography>
            </Stack>
          </CustomCardContent>
        </Grid>
        {lessonExtension ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Lesson extension
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {lessonExtension}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        {lessonExtensionReason ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Lesson extension reason
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {lessonExtensionReason}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        {feedback.studentAttentiveness ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Student&apos;s attentiveness
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {feedback.studentAttentiveness}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        {feedback.graspingAndApplicationOfConceptTaught ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Grasping and application of concepts taught
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {feedback.graspingAndApplicationOfConceptTaught}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        {feedback.progressForThisTopicAndSyllabus ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Progress for this topic and syllabus
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {feedback.progressForThisTopicAndSyllabus}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        {feedback.homeworkCompletedFromPreviousLesson ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Homework completed from previous lesson
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {feedback.homeworkCompletedFromPreviousLesson}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        {feedback.quizScore && feedback.quizScoreOutOf ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Quiz score
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {feedback.quizScore} / {feedback.quizScoreOutOf}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        {feedback.comments ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center">
                <Typography fontSize="16px" fontWeight={600} mb="7px">
                  Detailed feedback
                </Typography>
                <Typography fontSize="14px" fontWeight={500} color="#647488">
                  {feedback.comments}
                </Typography>
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center" gap="10px">
              <Typography fontWeight={500} fontSize="14px" color="#647488">
                Created at: {createdAt}
              </Typography>
              <Typography fontWeight={500} fontSize="14px" color="#647488">
                Last updated at: {lastUpdatedAt}
              </Typography>
              <Chip
                key={feedbackTutor._id}
                label={getShortName(
                  feedbackTutor.firstname,
                  feedbackTutor.lastname
                )}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
      </Grid>
    </div>
  );
};

export default ViewTutorFeedbackForm;
